// Client-side page-screenshot capture for the feedback widget (Unit 4).
//
// Uses modern-screenshot, which renders the DOM via an SVG <foreignObject> that
// the browser itself rasterizes -- so it supports whatever the app renders
// (modern CSS, the fixed `inset-0` shell layout) rather than reimplementing a
// renderer the way html2canvas did (html2canvas silently failed on this app and
// fell back to text-only). The library is lazy-loaded via dynamic import only
// when the user opts in, so it never enters the main bundle.
//
// The capture is downscaled (longest viewport edge -> MAX_EDGE) and
// JPEG-compressed, then dropped if it still exceeds MAX_BYTES, so a feedback
// POST can never carry an unbounded payload. Every failure path resolves to
// null so the caller falls back to a text-only submission.

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.7;
// Hard ceiling on the encoded bytes. Sits below the server's ~1.5 MB domain cap
// so a marginally-large capture is dropped here rather than 400'd server-side.
const MAX_BYTES = 1_000_000;
const CONTENT_TYPE = "image/jpeg";

export interface CapturedScreenshot {
  // Raw base64 (no `data:` URL prefix) -- the shape the API expects.
  base64: string;
  contentType: string;
}

// Attribute marking elements the capture must skip (e.g. the floating feedback
// button), so the screenshot shows the page behind the widget.
export const FEEDBACK_OVERLAY_ATTR = "data-feedback-overlay";

// Elements excluded from the capture: anything tagged with the overlay attr,
// plus the open dialog's Radix backdrop + content (the dim layer would wash out
// the whole shot, and the dialog itself is not what the user is reporting on).
const IGNORE_SELECTOR = `[${FEEDBACK_OVERLAY_ATTR}],[data-slot="dialog-overlay"],[data-slot="dialog-content"]`;

function captureScale(): number {
  if (typeof window === "undefined") return 1;
  const longest = Math.max(window.innerWidth, window.innerHeight) || MAX_EDGE;
  return longest > MAX_EDGE ? MAX_EDGE / longest : 1;
}

// Capture the current page as a compressed JPEG, or null on any failure / when
// the result is too large. Best-effort: never throws.
export async function captureScreenshot(): Promise<CapturedScreenshot | null> {
  if (typeof document === "undefined") return null;
  try {
    const { domToJpeg } = await import("modern-screenshot");
    const dataUrl = await domToJpeg(document.body, {
      quality: JPEG_QUALITY,
      scale: captureScale(),
      backgroundColor: "#ffffff",
      width: window.innerWidth,
      height: window.innerHeight,
      filter: (node) => !(node instanceof Element && node.matches(IGNORE_SELECTOR)),
    });
    const comma = dataUrl.indexOf(",");
    if (comma === -1) return null;
    const base64 = dataUrl.slice(comma + 1);
    // base64 length * 3/4 ~= decoded byte count.
    if (base64.length === 0 || base64.length * 0.75 > MAX_BYTES) return null;
    return { base64, contentType: CONTENT_TYPE };
  } catch {
    return null;
  }
}
